import { BUILDING_TYPES, type CaseListItemDto, type MeDto, SITE_ROLES, type SiteDto, type SiteRole } from '@bqcrm/shared';
import { ActionIcon, Anchor, Button, Group, Modal, Select, SimpleGrid, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBriefcase, IconInfoCircle, IconPencil, IconPlus, IconRoad, IconUnlink, IconUsers } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { ask } from '../../confirm';
import { PageHeader } from '../../components/PageHeader';
import { PartyPicker } from '../../components/PartyPicker';
import { QueryState } from '../../components/QueryState';
import { SectionCard } from '../../components/ui';
import { notifyError, notifyOk } from '../../notify';
import { CaseMiniList } from './PartyPage';
import { SiteForm } from './PartyForms';

type SiteDetail = SiteDto & { cases: CaseListItemDto[] };

export function SitePage({ me }: { me: MeDto }) {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['site', id], queryFn: () => api<SiteDetail>(`/sites/${id}`) });
  const [editing, edit] = useDisclosure();
  const [linking, link] = useDisclosure();
  const [partyId, setPartyId] = useState<string | null>(null);
  const [role, setRole] = useState<SiteRole>('uzytkownik');
  const qc = useQueryClient();
  const canEdit = can(me, 'klienci.edycja');
  const refresh = () => void qc.invalidateQueries({ queryKey: ['site', id] });
  const addLink = useMutation({
    mutationFn: () => api(`/sites/${id}/links`, { method: 'POST', body: { partyId, role } }),
    onSuccess: () => {
      notifyOk('Powiązano klienta');
      refresh();
      link.close();
      setPartyId(null);
    },
    onError: notifyError,
  });
  const removeLink = useMutation({
    mutationFn: (l: { partyId: string; role: SiteRole }) => api(`/sites/${id}/links/remove`, { method: 'POST', body: l }),
    onSuccess: () => {
      notifyOk('Odłączono klienta od lokalizacji');
      refresh();
    },
    onError: notifyError,
  });

  const first = q.data?.links[0];

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {q.data && (
        <>
          <PageHeader
            crumbs={[{ label: 'Klienci', to: '/klienci' }, ...(first ? [{ label: first.partyName, to: `/klienci/${first.partyId}` }] : []), { label: q.data.address }]}
            title={q.data.address}
            description={
              [q.data.buildingType && BUILDING_TYPES[q.data.buildingType], q.data.usableArea && `${q.data.usableArea} m² powierzchni użytkowej`].filter(Boolean).join(' · ') || 'Lokalizacja'
            }
            actions={
              canEdit && (
                <Button variant="light" leftSection={<IconPencil size={16} />} onClick={edit.open}>
                  Edytuj
                </Button>
              )
            }
          />

          <SimpleGrid cols={{ base: 1, md: 3 }} mb="lg">
            <SectionCard title="Dojazd, dostęp" icon={<IconRoad size={16} />}>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} c={q.data.accessNotes ? undefined : 'dimmed'}>
                {q.data.accessNotes || '—'}
              </Text>
            </SectionCard>
            <SectionCard title="Informacje techniczne" icon={<IconInfoCircle size={16} />}>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} c={q.data.technicalNotes ? undefined : 'dimmed'}>
                {q.data.technicalNotes || '—'}
              </Text>
            </SectionCard>
            <SectionCard
              title="Powiązani klienci"
              icon={<IconUsers size={16} />}
              actions={
                canEdit && (
                  <Button size="xs" variant="subtle" leftSection={<IconPlus size={14} />} onClick={link.open}>
                    Powiąż klienta
                  </Button>
                )
              }
            >
              {q.data.links.map((l) => (
                <Group key={`${l.partyId}-${l.role}`} justify="space-between" py={2} wrap="nowrap">
                  <Text size="sm">
                    <Anchor component={Link} to={`/klienci/${l.partyId}`}>
                      {l.partyName}
                    </Anchor>{' '}
                    <Text span c="dimmed" size="xs">
                      {SITE_ROLES[l.role]}
                    </Text>
                  </Text>
                  {canEdit && q.data.links.length > 1 && (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      aria-label="Odłącz"
                      onClick={() => {
                        void ask(`Klient „${l.partyName}” (${SITE_ROLES[l.role].toLowerCase()}) nie będzie już powiązany z tą lokalizacją.`, {
                          title: 'Odłączyć klienta od lokalizacji?',
                          confirm: 'Odłącz',
                          danger: true,
                        }).then((ok) => ok && removeLink.mutate({ partyId: l.partyId, role: l.role }));
                      }}
                    >
                      <IconUnlink size={14} />
                    </ActionIcon>
                  )}
                </Group>
              ))}
            </SectionCard>
          </SimpleGrid>

          <SectionCard title={`Sprawy w tej lokalizacji (${q.data.cases.length})`} icon={<IconBriefcase size={16} />} padding={0}>
            <CaseMiniList items={q.data.cases} />
          </SectionCard>

          <Modal opened={editing} onClose={edit.close} title="Edycja lokalizacji" size="lg">
            <SiteForm
              site={q.data}
              onCancel={edit.close}
              onDone={() => {
                edit.close();
                refresh();
              }}
            />
          </Modal>
          <Modal opened={linking} onClose={link.close} title="Powiązanie klienta z lokalizacją">
            <Stack>
              <PartyPicker label="Klient" value={partyId} onChange={setPartyId} />
              <Select label="Rola" data={Object.entries(SITE_ROLES).map(([value, label]) => ({ value, label }))} value={role} onChange={(v) => v && setRole(v as SiteRole)} allowDeselect={false} />
              <Group justify="flex-end">
                <Button variant="default" onClick={link.close}>
                  Anuluj
                </Button>
                <Button onClick={() => addLink.mutate()} loading={addLink.isPending} disabled={!partyId}>
                  Powiąż
                </Button>
              </Group>
            </Stack>
          </Modal>
        </>
      )}
    </QueryState>
  );
}
